/**
 * Helpers for keeping `user_notification_browser_state` in sync with the
 * browser's actual `Notification.permission` value, plus platform-specific
 * recovery instructions.
 *
 * Called from authStore on every mount with an authenticated user. If the
 * stored state diverges from the live browser state, we update the row
 * and (when re-granted) silently re-subscribe so push delivery resumes
 * without a manual step.
 */

import { supabase } from './supabase';
import {
    getNotificationPermissionState,
    getCurrentPushSubscription,
    subscribeToPush,
    isStandalone,
    isIOS,
} from './pwaInstall';
import { savePushSubscription } from './notificationPreferences';

export type SyncResult =
    | { changed: false }
    | { changed: true; from: string; to: 'granted' | 'denied' | 'default' };

/**
 * Read browser permission, compare with stored state, write back if
 * different. Returns whether anything changed so callers can show a
 * toast on the granted ← denied transition.
 *
 * Silent on every failure path. The recovery banner is the long-tail
 * surface; this is just bookkeeping.
 */
export async function syncPermissionState(): Promise<SyncResult> {
    const live = getNotificationPermissionState();
    if (live === 'unsupported') return { changed: false };

    const {
        data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { changed: false };

    const { data: existing } = await supabase
        .from('user_notification_browser_state')
        .select('permission_state, permission_denied_at')
        .eq('user_id', user.id)
        .maybeSingle();

    const stored = existing?.permission_state || 'default';
    if (stored === live) {
        return { changed: false };
    }

    const update: Record<string, unknown> = {
        user_id: user.id,
        permission_state: live,
        last_permission_sync: new Date().toISOString(),
    };

    // Only stamp permission_denied_at on the granted/default → denied
    // transition. Don't reset it on denied → granted; we keep the
    // history for analytics + the 14-day recovery cooldown.
    if (live === 'denied' && stored !== 'denied') {
        update.permission_denied_at = new Date().toISOString();
    }

    const { error } = await supabase
        .from('user_notification_browser_state')
        .upsert(update, { onConflict: 'user_id' });

    if (error) {
        console.warn('[notificationPermission] sync upsert failed:', error);
        return { changed: false };
    }

    // On denied → granted, silently re-subscribe so the user can stop
    // receiving emails immediately. Caller decides whether to show a toast.
    if (live === 'granted' && stored === 'denied') {
        const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as
            | string
            | undefined;
        if (vapidKey) {
            try {
                const existingSub = await getCurrentPushSubscription();
                if (!existingSub) {
                    const sub = await subscribeToPush(vapidKey);
                    if (sub) await savePushSubscription(sub);
                } else {
                    await savePushSubscription(existingSub);
                }
            } catch (err) {
                console.warn(
                    '[notificationPermission] re-subscribe after re-grant failed:',
                    err,
                );
            }
        }
    }

    return { changed: true, from: stored, to: live as 'granted' | 'denied' | 'default' };
}

/**
 * Read the browser-state row WITHOUT writing — used by the recovery
 * banner to check the 14-day cooldown.
 */
export async function getStoredBrowserState(): Promise<{
    permission_state: 'default' | 'granted' | 'denied';
    permission_denied_at: string | null;
} | null> {
    const {
        data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
        .from('user_notification_browser_state')
        .select('permission_state, permission_denied_at')
        .eq('user_id', user.id)
        .maybeSingle();

    if (error || !data) return null;
    return data as {
        permission_state: 'default' | 'granted' | 'denied';
        permission_denied_at: string | null;
    };
}

export interface RecoveryInstruction {
    title: string;
    steps: string[];
}

/**
 * Platform-specific recovery instructions. The banner opens a modal that
 * renders these steps so the user can manually re-enable notifications
 * in their browser/OS settings.
 */
export function getRecoveryInstructions(): RecoveryInstruction {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const standalone = isStandalone();

    if (isIOS()) {
        if (standalone) {
            return {
                title: 'Activar avisos en tu iPhone',
                steps: [
                    'Abre Ajustes en tu iPhone.',
                    'Pulsa Notificaciones.',
                    'Busca Ethernal en la lista y entra.',
                    'Activa Permitir notificaciones.',
                ],
            };
        }
        return {
            title: 'Abre Ethernal desde el icono de inicio',
            steps: [
                'Cierra esta pestaña y abre Ethernal desde el icono que añadiste a tu pantalla de inicio.',
                'La primera vez te pedirá permiso para los avisos. Acepta.',
                'Si no ves el icono, vuelve a instalarlo desde el menú Compartir → Añadir a pantalla de inicio.',
            ],
        };
    }

    const isAndroid = /Android/i.test(ua);
    if (isAndroid) {
        return {
            title: 'Activar avisos en Chrome Android',
            steps: [
                'Pulsa el candado en la barra de direcciones, junto a la URL.',
                'Entra en Permisos del sitio.',
                'Busca Notificaciones y elige Permitir.',
                'Recarga Ethernal y vuelve a entrar.',
            ],
        };
    }

    const isFirefox = /Firefox/i.test(ua);
    if (isFirefox) {
        return {
            title: 'Activar avisos en Firefox',
            steps: [
                'Pulsa el icono del candado a la izquierda de la barra de direcciones.',
                'En Permisos, busca Recibir notificaciones.',
                'Quita el bloqueo y elige Permitir.',
                'Recarga Ethernal.',
            ],
        };
    }

    // Chrome desktop / generic Chromium fallback.
    return {
        title: 'Activar avisos en el navegador',
        steps: [
            'Pulsa el candado a la izquierda de la barra de direcciones.',
            'Elige Configuración del sitio.',
            'En Notificaciones, elige Permitir.',
            'Recarga Ethernal.',
        ],
    };
}

/**
 * Decide whether the recovery banner should render right now. Centralised
 * so both the dashboard render and any future analytics reuse the same
 * logic.
 *
 * Triggers:
 *   - permission is denied at the browser level
 *   - permission_denied_at + 14 days < now() (cooldown)
 *   - user hasn't dismissed it in the current session
 */
export function shouldShowRecoveryBanner(state: {
    permission_state: 'default' | 'granted' | 'denied';
    permission_denied_at: string | null;
}): boolean {
    if (state.permission_state !== 'denied') return false;
    if (typeof window === 'undefined') return false;
    if (sessionStorage.getItem('ethernal_recovery_dismissed')) return false;
    if (!state.permission_denied_at) return false;

    const denied = new Date(state.permission_denied_at).getTime();
    const fourteenDays = 14 * 24 * 60 * 60 * 1000;
    return Date.now() - denied >= fourteenDays;
}
